import type { ShopProduct } from '../../commerce/shopify.types';

/**
 * Carrousel de photos en tête de fiche, comme sur la fiche produit du site.
 *
 * Il repose sur le défilement natif avec accroche : c'est ce que fait un doigt
 * sur mobile, sans script, sans inertie réimplémentée, et cela reste
 * parcourable au clavier. Les pastilles servent aussi de commandes.
 */
export interface ProductCarousel {
  element: HTMLElement;
  destroy(): void;
}

export function createProductCarousel(product: ShopProduct): ProductCarousel | null {
  const photos = product.images.length ? product.images : [];
  if (photos.length === 0) return null;

  const element = document.createElement('div');
  element.className = 'product-carousel';

  const piste = document.createElement('div');
  piste.className = 'product-carousel__track';
  piste.setAttribute('role', 'group');
  piste.setAttribute('aria-label', `Photos de ${product.title}`);

  photos.forEach((photo, index) => {
    const vignette = document.createElement('figure');
    vignette.className = 'product-carousel__slide';
    const image = document.createElement('img');
    image.src = photo.url;
    // Seule la première photo décrit le produit ; répéter la description à
    // chaque vue ferait dire trois fois la même chose à un lecteur d'écran.
    image.alt = index === 0 ? photo.alt || product.title : '';
    image.loading = index === 0 ? 'eager' : 'lazy';
    image.decoding = 'async';
    vignette.append(image);
    piste.append(vignette);
  });

  element.append(piste);

  let pastilles: HTMLButtonElement[] = [];
  let onScroll: (() => void) | null = null;

  if (photos.length > 1) {
    const barre = document.createElement('div');
    barre.className = 'product-carousel__dots';

    pastilles = photos.map((_, index) => {
      const point = document.createElement('button');
      point.type = 'button';
      point.className = 'product-carousel__dot';
      point.setAttribute('aria-label', `Photo ${index + 1} sur ${photos.length}`);
      point.addEventListener('click', () => {
        piste.scrollTo({ left: index * piste.clientWidth, behavior: 'smooth' });
      });
      barre.append(point);
      return point;
    });

    const marquer = (index: number): void => {
      pastilles.forEach((point, rang) => {
        point.classList.toggle('is-active', rang === index);
        point.setAttribute('aria-current', String(rang === index));
      });
    };
    marquer(0);

    // Une seule lecture par image, comme dans l'allée : le défilement tactile
    // émet des dizaines d'événements par seconde.
    let planifie = false;
    onScroll = (): void => {
      if (planifie) return;
      planifie = true;
      requestAnimationFrame(() => {
        planifie = false;
        const largeur = piste.clientWidth || 1;
        marquer(Math.round(piste.scrollLeft / largeur));
      });
    };
    piste.addEventListener('scroll', onScroll, { passive: true });

    element.append(barre);
  }

  return {
    element,
    destroy(): void {
      if (onScroll) piste.removeEventListener('scroll', onScroll);
    },
  };
}
